import { getLocale } from "@/languages";
import type { SubscriptionManagementProvider } from "@/types";

// Every language the marketing site publishes a translated page for. Both
// /dictation/ and /apps/ exist in all of them, so one set covers both — an
// unlisted language falls back to the English URL rather than deep-linking
// into a 404.
const MARKETING_SITE_LOCALES: ReadonlySet<string> = new Set([
  "es",
  "fr",
  "it",
  "pt",
  "de",
  "ja",
  "ar",
  "ko",
  "pl",
  "nl",
  "tr",
  "da",
  "no",
  "vi",
  "id",
  "uk",
  "cs",
  "sv",
  "ro",
  "sl",
  "el",
  "fi",
  "he",
  "zh",
  "ru",
  "hi",
  "hu",
  "bn",
  "hr",
  "lt",
  "et",
  "bg",
  "sr",
  "sw",
  "th",
]);

// Localized URL for the desktop dictation product (Mac/Windows). The
// English variant lives at the root path (no language segment); every
// other supported locale lands under `/<lang>/dictation/`. Unknown locales
// fall back to English so we never deep-link to a marketing-site 404.
// Used by save-attempts and in-app cross-sells — single source of truth.
export const getDictationWebUrl = (): string => {
  const base = "https://1transcribe.com";
  const lang = (getLocale() || "en").slice(0, 2).toLowerCase();
  if (!lang || lang === "en") return `${base}/dictation/`;
  if (!MARKETING_SITE_LOCALES.has(lang)) return `${base}/dictation/`;
  return `${base}/${lang}/dictation/`;
};

// One stable cross-device handoff page. Mobile settings opens this instead of
// trying to download a desktop installer that the current phone cannot use.
export const getAppsWebUrl = (): string => {
  const base = "https://1transcribe.com";
  const lang = (getLocale() || "en").slice(0, 2).toLowerCase();
  if (!lang || lang === "en") return `${base}/apps/`;
  if (!MARKETING_SITE_LOCALES.has(lang)) return `${base}/apps/`;
  return `${base}/${lang}/apps/`;
};

export const ANDROID_APP_LINK =
  "https://play.google.com/store/apps/details?id=com.transcribe.audio.to.text";
export const APP_ID = "6705123880";
export const IOS_APP_LINK = `https://apps.apple.com/us/app/${APP_ID}`;

export const SUBSCRIPTION_MANAGEMENT_URLS: Readonly<
  Record<Exclude<SubscriptionManagementProvider, "stripe">, string>
> = {
  googlePlay: "https://play.google.com/store/account/subscriptions",
  appStore: "https://apps.apple.com/account/subscriptions",
};

export const getExternalSubscriptionManagementUrl = (
  provider: SubscriptionManagementProvider,
): string | undefined =>
  provider === "stripe" ? undefined : SUBSCRIPTION_MANAGEMENT_URLS[provider];

export type AppDownloadPlatform = "android" | "ios" | "mac" | "windows";

export const APP_DOWNLOAD_URLS: Readonly<Record<AppDownloadPlatform, string>> =
  {
    android: ANDROID_APP_LINK,
    ios: IOS_APP_LINK,
    mac: "https://app-releases.1transcribe.com/desktop/1Transcribe-Dictation-latest-mac.dmg",
    windows:
      "https://app-releases.1transcribe.com/desktop/1Transcribe-Dictation-latest-windows.exe",
  };

// The one app this device can install, or undefined when the device matches
// none (link the page listing them all instead). iPadOS reports a Mac user
// agent, so a "Mac" with a touch screen is an iPad.
export const getDeviceAppPlatform = ({
  userAgent,
  maxTouchPoints,
}: {
  userAgent: string;
  maxTouchPoints: number;
}): AppDownloadPlatform | undefined => {
  const isMac = /Macintosh/.test(userAgent);
  if (/iPhone|iPad|iPod/.test(userAgent) || (isMac && maxTouchPoints > 1)) {
    return "ios";
  }
  if (/Android/.test(userAgent)) return "android";
  if (isMac) return "mac";
  if (/Windows/.test(userAgent)) return "windows";
  return undefined;
};

export type EmailDraft = {
  to: string;
  subject: string;
  body: string;
};

export const buildEmailDraftUrl = ({ to, subject, body }: EmailDraft): string =>
  `mailto:${encodeURIComponent(to.trim())}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;

export const openEmailDraft = (draft: EmailDraft) => {
  window.open(buildEmailDraftUrl(draft), "_blank");
};

export const gotoTerms = () => {
  window.open("https://1transcribe.com/legal/", "_blank");
};

export const gotoPrivacy = () => {
  window.open("https://1transcribe.com/legal/", "_blank");
};

export const contactUs = (subject?: string, body?: string) => {
  if (subject) {
    window.open(
      `mailto:support@1transcribe.com?subject=${subject}&body=${body || ""}`,
      "_blank",
    );
  } else {
    window.open(
      `mailto:support@1transcribe.com?subject=${subject}&body=${body || ""}`,
      "_blank",
    );
  }
};
